import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { hubOpportunitiesInfiniteQueryOptions } from "@/lib/queries";

import { toHubChromeOpportunityRow } from "../../../lib/hub-row-mappers.js";
import type { HubChromeOpportunityRow } from "../PersonaOpportunityHubChrome/hub-chrome.types.js";
import type { HubOpportunityServerQuery } from "../PersonaOpportunityHubChrome/hub-opportunity-window.js";

export interface OpportunityGroupSlice {
  groupKey: string;
  rows: HubChromeOpportunityRow[];
  hasMore: boolean;
  isLoading: boolean;
}

/**
 * One infinite query for a single expanded (or one-row) group. The hub mounts
 * this only for groups the virtualizer has asked to see.
 */
export function OpportunityGroupLoader({
  tenantId,
  setIds,
  query,
  groupKey,
  onSlice,
  registerFetcher,
}: {
  tenantId: string | null;
  setIds: readonly string[];
  query: HubOpportunityServerQuery;
  groupKey: string;
  onSlice: (slice: OpportunityGroupSlice) => void;
  registerFetcher: (groupKey: string, fetchNext: () => void) => () => void;
}) {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const result = useInfiniteQuery({
    ...hubOpportunitiesInfiniteQueryOptions(
      tenantId,
      setIds,
      { groupBy: query.groupBy, groupKey, filters: query.filters },
      { enabled: true },
    ),
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(
    () =>
      (result.data?.pages ?? []).flatMap((page) =>
        page.items.map((row) => toHubChromeOpportunityRow(row, tRef.current)),
      ),
    [result.data],
  );
  const onSliceRef = useRef(onSlice);
  onSliceRef.current = onSlice;

  useEffect(() => {
    onSliceRef.current({
      groupKey,
      rows,
      hasMore: result.hasNextPage,
      // Placeholder pages belong to the old sort, so paging waits for the new first page.
      isLoading: result.isLoading || result.isFetchingNextPage || result.isPlaceholderData,
    });
  }, [
    groupKey,
    rows,
    result.hasNextPage,
    result.isLoading,
    result.isFetchingNextPage,
    result.isPlaceholderData,
  ]);

  useEffect(() => {
    return registerFetcher(groupKey, () => {
      void result.fetchNextPage();
    });
  }, [groupKey, registerFetcher, result.fetchNextPage]);

  return null;
}
